import { useMediaLibrary } from "@/hooks/use-media-library";
import { MediaAsset } from "@/types/media";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "../ui/button";
import { MediaGrid } from "./MediaGrid";
import { MediaPreviewDialog } from "./MediaPreviewDialog";

export function MediaGridPane({
  state,
  multi = true,
  onCopyUrl,
  onDelete,
}: {
  state: ReturnType<typeof useMediaLibrary>;
  multi?: boolean;
  onCopyUrl: (item: MediaAsset) => Promise<void> | void;
  onDelete?: (item: MediaAsset) => Promise<void> | void;
}) {
  const {
    mediaAssets,
    loadingAssets,
    selectedIds,
    toggleSelect,
    hasMore,
    fetchMediaAssets,
    setPage,
    currentParentId,
  } = state;
  const [previewItem, setPreviewItem] = useState<MediaAsset | null>(null);

  if (loadingAssets && mediaAssets.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-slate-500">
        <div className="flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (mediaAssets.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-slate-500 text-sm">
        No media found.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-3 sm:p-4">
      <MediaGrid
        items={mediaAssets}
        selectable
        selectedIds={selectedIds}
        onToggleSelect={(id) => toggleSelect(id, multi)}
        onPreview={(item) => setPreviewItem(item)}
        onCopyUrl={onCopyUrl}
        onDelete={onDelete}
      />

      {/* Load more */}
      {hasMore && (
        <div className="flex justify-center pt-1">
          <Button
            variant="outline"
            size="sm"
            disabled={loadingAssets}
            onClick={() => {
              setPage((p) => p + 1);
              fetchMediaAssets({ parentId: currentParentId });
            }}
          >
            {loadingAssets ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Load more"
            )}
          </Button>
        </div>
      )}

      <MediaPreviewDialog
        item={previewItem}
        open={!!previewItem}
        onOpenChange={(open) => !open && setPreviewItem(null)}
      />
    </div>
  );
}
